/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
/**
 * Pure helpers for the per-row capability editor on the model field-sync page.
 *
 * The `capabilities` JSON is a rich structure; the editor only touches
 * modalities, a small set of feature keys, and two limits. Everything else
 * (provider, source, unknown features, other limits) MUST survive a round-trip,
 * which is why merging here is "surgical" rather than a blind overwrite.
 */

export const EDITABLE_FEATURES = [
  'tools',
  'reasoning',
  'files',
  'audio',
  'structured_output',
] as const

export type EditableFeature = (typeof EDITABLE_FEATURES)[number]

export interface CapabilityEditValues {
  supportsVision: boolean | null
  contextWindow: number | null
  maxOutputTokens: number | null
  inputModalities: string[]
  outputModalities: string[]
  features: string[] // subset of EDITABLE_FEATURES
}

type Dict = Record<string, unknown>

function isPlainObject(value: unknown): value is Dict {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value)
  )
}

/**
 * string | object | null → object. Returns {} on parse failure, empty input,
 * or any non-object value. Never returns null.
 */
export function normalizeCapabilities(input: unknown): Record<string, unknown> {
  if (input == null) return {}
  if (typeof input === 'string') {
    const trimmed = input.trim()
    if (trimmed === '') return {}
    try {
      const parsed = JSON.parse(trimmed)
      return isPlainObject(parsed) ? parsed : {}
    } catch {
      return {}
    }
  }
  return isPlainObject(input) ? input : {}
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string')
}

/**
 * Surgically merge edit values into an existing capabilities object/string,
 * preserving everything the editor does not own.
 */
export function buildCapabilities(
  existing: unknown,
  v: CapabilityEditValues,
): Record<string, unknown> {
  const caps: Dict = { ...normalizeCapabilities(existing) }

  // modalities — always write explicit arrays, keep any other sub-keys.
  const existingModalities = isPlainObject(caps.modalities) ? caps.modalities : {}
  caps.modalities = {
    ...existingModalities,
    input: v.inputModalities,
    output: v.outputModalities,
  }

  // features — start from existing object, edit only EDITABLE_FEATURES keys.
  // Legacy shape: features can be a string array (e.g. ['open_weights']).
  // Normalize each array entry to a feature record so non-editable keys survive.
  const features: Dict = isPlainObject(caps.features)
    ? { ...caps.features }
    : Array.isArray(caps.features)
      ? Object.fromEntries(
          asStringArray(caps.features).map((entry) => [
            entry,
            { supported: true, api_available: true },
          ]),
        )
      : {}
  const checked = new Set(v.features)
  for (const key of EDITABLE_FEATURES) {
    if (checked.has(key)) {
      const prev = isPlainObject(features[key]) ? features[key] : {}
      features[key] = { ...prev, supported: true, api_available: true }
    } else {
      delete features[key]
    }
  }
  caps.features = features

  // limits — overwrite the two editable limits, preserve the rest.
  const existingLimits = isPlainObject(caps.limits) ? caps.limits : {}
  caps.limits = {
    ...existingLimits,
    context_window: v.contextWindow,
    max_output_tokens: v.maxOutputTokens,
  }

  // schema_version: backfill if missing.
  if (caps.schema_version == null) {
    caps.schema_version = 1
  }

  // source: backfill a manual provenance only if absent; never touch existing.
  if (!isPlainObject(caps.source)) {
    caps.source = { type: 'manual', confidence: 'high' }
  }

  return caps
}

/**
 * Reverse-extract form values from a model's flat fields + capabilities JSON.
 * Only EDITABLE_FEATURES whose record `.supported === true` map to form toggles.
 */
export function extractEditValues(source: {
  capabilities?: unknown
  supports_vision?: boolean | null
  context_window?: number | null
  max_output_tokens?: number | null
}): CapabilityEditValues {
  const caps = normalizeCapabilities(source.capabilities)

  const modalities = isPlainObject(caps.modalities) ? caps.modalities : {}
  const inputModalities = asStringArray(modalities.input)
  const outputModalities = asStringArray(modalities.output)

  // features may be an object map (records) or a legacy string array of keys.
  const arrayFeatureKeys = new Set(asStringArray(caps.features))
  const capsFeatures = isPlainObject(caps.features) ? caps.features : {}
  const features = EDITABLE_FEATURES.filter((key) => {
    if (arrayFeatureKeys.has(key)) return true
    const record = capsFeatures[key]
    return isPlainObject(record) && record.supported === true
  })

  return {
    supportsVision: source.supports_vision ?? null,
    contextWindow: source.context_window ?? null,
    maxOutputTokens: source.max_output_tokens ?? null,
    inputModalities,
    outputModalities,
    features,
  }
}
